import { useCallback } from 'react';
import { useStore } from 'react-admin';

import { TransactionMonitoringThresholds } from '../utilities/schemas/datapoints/transactionMonitoringThresholds';

// Kept as a plain object (not a Map) so it survives JSON serialization by the localStorage store
export type OverriddenExpectedBehaviours = Record<string, TransactionMonitoringThresholds>;

const UNSPECIFIED_TYPE_CONFIGURATION_KEY = 'undefined';

const toStoreKey = (typeConfigurationKey: string | undefined) => typeConfigurationKey
  ?? UNSPECIFIED_TYPE_CONFIGURATION_KEY;

export const getOverride = (
  overrides: OverriddenExpectedBehaviours | undefined,
  typeConfigurationKey: string | undefined,
): TransactionMonitoringThresholds | undefined => overrides?.[toStoreKey(typeConfigurationKey)];

export const withOverride = (
  overrides: OverriddenExpectedBehaviours | undefined,
  typeConfigurationKey: string | undefined,
  value: TransactionMonitoringThresholds | undefined,
): OverriddenExpectedBehaviours => {
  const newOverrides = { ...overrides };
  if (value) {
    newOverrides[toStoreKey(typeConfigurationKey)] = value;
  } else {
    delete newOverrides[toStoreKey(typeConfigurationKey)];
  }
  return newOverrides;
};

const useOverriddenExpectedBehaviours = (entityId: string) => {
  const [
    overriddenExpectedBehaviours,
    setOverriddenExpectedBehaviours,
  ] = useStore<OverriddenExpectedBehaviours>(`transactionOverriddenExpectedBehaviours-${entityId}`, {});

  return {
    getOverride: useCallback(
      (typeConfigurationKey: string | undefined) => getOverride(
        overriddenExpectedBehaviours,
        typeConfigurationKey,
      ),
      [overriddenExpectedBehaviours],
    ),
    setOverride: useCallback((
      typeConfigurationKey: string | undefined,
      value: TransactionMonitoringThresholds | undefined,
    ) => {
      setOverriddenExpectedBehaviours(
        (currentOverrides) => withOverride(currentOverrides, typeConfigurationKey, value),
      );
    }, [setOverriddenExpectedBehaviours]),
    resetOverrides: useCallback(() => {
      setOverriddenExpectedBehaviours({});
    }, [setOverriddenExpectedBehaviours]),
  };
};

export default useOverriddenExpectedBehaviours;
